import { useSearchParams } from "react-router-dom";
import { User, KeyRound, Webhook, Bell, MessageCircle, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { useSettings } from "./use-settings";
import { ApiKeysTab } from "./api-keys-tab";
import { WebhooksTab } from "./webhooks-tab";
import { ProfileTab } from "./profile-tab";
import { AlertsTab } from "./alerts-tab";

import { useAuth } from "../../context/AuthContext";
import { canAccessWhatsApp } from "../../lib/userAccess";
import WhatsAppInstancesPanel from "../admin/WhatsAppInstancesPanel";

const ALL_SECTIONS: { id: string; label: string; icon: LucideIcon }[] = [
  { id: "profile", label: "Profile", icon: User },
  { id: "keys", label: "API Keys", icon: KeyRound },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "alerts", label: "Alert", icon: Bell },
];

export function SettingsPage() {
  const s = useSettings();
  const { token, user } = useAuth() as any;
  // Off by default; granted per account from the TechLadder admin panel
  // (whatsapp_feature), same shape as the Workflows/CRM/Chatbot nav gating.
  const SECTIONS = ALL_SECTIONS.filter(item => item.id !== "whatsapp" || canAccessWhatsApp(user));
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get("section");
  const section = SECTIONS.some(item => item.id === requestedSection) ? requestedSection : "profile";
  const setSection = (id: string) => { const next = new URLSearchParams(searchParams); next.set("section", id); setSearchParams(next, { replace: true }); };

  return (
    <div className="space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      <div>
        <h1 className="text-sm font-semibold text-ink">Settings</h1>
        
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Section nav */}
        <nav className="h-fit rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-1.5 lg:sticky lg:top-8">
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                section === id ? "bg-accent-50 font-semibold text-accent-700 dark:bg-accent-500/10 dark:text-accent-400" : "text-ink-2 hover:bg-soft hover:text-ink",
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="lg:col-span-3">
          {section === "profile" && <ProfileTab />}
          {section === "keys" && <ApiKeysTab />}
          {section === "webhooks" && (
            <WebhooksTab webhooks={s.webhooks} onAdd={s.addWebhook} onToggle={s.toggleWebhook} onDelete={s.deleteWebhook} />
          )}
          {section === "whatsapp" && token && user && (
            <WhatsAppInstancesPanel
              key={user.client_id || user.id}
              token={token}
              clientId={user.client_id || user.id}
              user={user}
            />
          )}
          {section === "alerts" && <AlertsTab />}
        </div>
      </div>
    </div>
  );
}
